import { Component, inject } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { catchError, defer, map, Notification, of } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FullPageMaskService, SpinnerModule } from 'common';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { AccountApiService } from '../../../services/account-api.service';
import { Status } from '../../../interfaces/status.interface';
import { Nullable } from '../../../interfaces/nullable.interface';
import { OPAStatus } from '../../../interfaces/opa-status.interface';
import { AllPendingSenderIdAuthorisationItem } from '../../../interfaces/all-pending-sender-id-authorisation-item.interface';
import { NotificationService } from '../../../services/notification.service';

type PendingOpaFormGroup = FormGroup<{
  id: FormControl<number>;
  selected: FormControl<Nullable<Status>>;
}>;

@Component({
  selector: 'si-pending-sender-ids-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatTableModule, CommonModule, MatIconModule, ReactiveFormsModule, MatRadioModule, SpinnerModule],
  providers: [],
  templateUrl: './pending-sender-ids-dialog.component.html',
  styleUrls: ['./pending-sender-ids-dialog.component.scss'],
})
export class PendingSenderIdsDialogComponent {
  public readonly columns = ['senderIdText', 'opa', 'actions'];

  private readonly maskService = inject(FullPageMaskService);
  private readonly accountApiService = inject(AccountApiService);
  private readonly dialogRef = inject(MatDialogRef<PendingSenderIdsDialogComponent>);
  public readonly fb = inject(FormBuilder);

  public OPAStatus = OPAStatus;
  public isFetching = true;
  public formArray: FormArray<PendingOpaFormGroup> = this.fb.array<PendingOpaFormGroup>([]);

  public dataSource$ = defer(() => {
    this.maskService.showLoading();
    return this.accountApiService.getAllPendingAuthorisationList$();
  }).pipe(
    map((data) => {
      this.maskService.hideLoading();
      this.isFetching = false;

      data.forEach(({ authorisationId }) => this.formArray.push(this.fb.group({
        id: new FormControl({ disabled: true, value: authorisationId }),
        selected: new FormControl(null),
      }), { emitEvent: false }));

      return new MatTableDataSource<AllPendingSenderIdAuthorisationItem>(data);
    }),
    catchError(() => {
      this.maskService.hideLoading();
      this.isFetching = false;
      return of(new MatTableDataSource<AllPendingSenderIdAuthorisationItem>([]));
    })
  );

  public getFormArrayControl(index: number) {
    return this.formArray.at(index).get('selected');
  }

  public handleClose() {
    this.dialogRef.close();
  }

  public handleFormSubmit() {
    const data = this.formArray.getRawValue().filter(({ selected }) => !!selected).map(({ id, selected }) => ({
      authorisationId: id,
      statusId: selected,
    }));

    this.dialogRef.close(data);
  }
}
